import { FilterLang } from '../ParsingLangs/FilterLang';
import { HiddenFieldsLang } from '../ParsingLangs/HiddenFieldsLang';
import { SortLang } from '../ParsingLangs/SortLang';
import {
  parseConfiguration,
  type FieldConfiguration,
} from '../configuration-parsers/parse-configuration';
import { CsvErrorCategory, type CsvErrorCollector } from '../csv-error-collector';
import {
  addHiddenField,
  editFieldSetting,
  editViewSetting,
  field,
  removeField,
  removeHiddenField,
  removeSort,
  removeView,
  removeFilter,
  view,
  layout,
  editSort,
} from '../tokens';
import {
  type AddFilterToken,
  type DatabaseSortDirection,
  type EditFilterToken,
  type ParsedRow,
  type SortToken,
  type Token,
  TokenType,
} from '../types';
import { stringToDatabaseLayoutType } from '../utils';

export function* parseEditMetadataRow(
  row: ParsedRow,
  errorCollector?: CsvErrorCollector
): Generator<Token> {
  try {
    const operationType = row['operation_type'];

    switch (operationType) {
      case 'add_field':
        yield parseAddFieldOperation(row, errorCollector);
        break;
      case 'edit_field':
        yield parseEditFieldOperation(row, errorCollector);
        break;
      case 'delete_field':
        yield parseDeleteFieldOperation(row, errorCollector);
        break;
      case 'edit_view':
        yield parseEditViewOperation(row, errorCollector);
        break;
      case 'add_sort':
        yield* parseAddSortOperation(row, errorCollector);
        break;
      case 'edit_sort':
        yield parseEditSortOperation(row, errorCollector);
        break;
      case 'delete_sort':
        yield parseDeleteSortOperation(row, errorCollector);
        break;
      case 'delete_view':
        yield parseDeleteViewOperation(row, errorCollector);
        break;
      case 'add_filter':
        yield parseAddFilterOperation(row, errorCollector);
        break;
      case 'edit_filter':
        yield parseEditFilterOperation(row, errorCollector);
        break;
      case 'delete_filter':
        yield parseDeleteFilterOperation(row, errorCollector);
        break;
      case 'add_view':
        yield* parseAddViewOperation(row, errorCollector);
        break;
      case 'add_hidden_field':
        yield parseAddHiddenFieldOperation(row, errorCollector);
        break;
      case 'remove_hidden_field':
        yield parseRemoveHiddenFieldOperation(row, errorCollector);
        break;
      default:
        if (operationType) {
          const errorMessage = `Unknown metadata operation_type: ${operationType}`;
          errorCollector?.addError(
            errorMessage,
            CsvErrorCategory.METADATA,
            `row: ${JSON.stringify(row)}`
          );
          console.error(errorMessage);
        }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error parsing edit metadata row: ${errorMessage}`,
      CsvErrorCategory.METADATA,
      `row: ${JSON.stringify(row)}`
    );
    console.error('Error parsing edit metadata row', error);
  }
}

function parseAddFieldOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    const target = row['target'];
    const property = row['property'];

    if (!target) {
      const errorMessage = 'Missing target field for add_field operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.FIELD,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
    }

    return field(target, property);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in add_field operation: ${errorMessage}`,
      CsvErrorCategory.FIELD,
      `target: ${row['target']}, property: ${row['property']}`
    );
    console.error('Error in add_field operation', error);
    throw error;
  }
}

function parseEditFieldOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    const target = row['target'];
    const property = row['property'];
    let value: string | boolean | FieldConfiguration = row['value'];

    if (!target) {
      const errorMessage = 'Missing target field for edit_field operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.FIELD,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
    }

    if (property === 'number_format' && typeof value === 'string') {
      const configString = `number_format:${value}`;
      const parsed = parseConfiguration(configString);
      if (parsed) {
        value = parsed;
      }
    }

    return editFieldSetting(target, property, value);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in edit_field operation: ${errorMessage}`,
      CsvErrorCategory.FIELD,
      `target: ${row['target']}, property: ${row['property']}, value: ${row['value']}`
    );
    console.error('Error in edit_field operation', error);
    throw error;
  }
}

function parseEditViewOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    const target = row['target'];
    const property = row['property'];
    const value = row['value'];

    if (!target) {
      const errorMessage = 'Missing target view for edit_view operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.VIEW,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
    }

    return editViewSetting(target, property, value);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in edit_view operation: ${errorMessage}`,
      CsvErrorCategory.VIEW,
      `target: ${row['target']}, property: ${row['property']}, value: ${row['value']}`
    );
    console.error('Error in edit_view operation', error);
    throw error;
  }
}

function* parseAddSortOperation(
  row: ParsedRow,
  errorCollector?: CsvErrorCollector
): Generator<Token> {
  const viewName = row['target'];
  const sortsString = row['value'];

  if (!sortsString) {
    return;
  }

  try {
    const tokens: Omit<SortToken, 'viewName'>[] =
      SortLang.Statement.tryParse(sortsString);

    for (const token of tokens) {
      yield { ...token, viewName };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error creating sort tokens for edit.add_sort: ${errorMessage}`,
      CsvErrorCategory.SORT,
      `target: ${viewName}, value: ${sortsString}`
    );
    console.error('Error creating sort tokens for edit.add_sort', error);
    return;
  }
}

function parseEditSortOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    const target = row['target'];
    const property = row['property'];
    const value = row['value'];

    if (!target || !property) {
      const errorMessage = 'Missing target or property for edit_sort operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.SORT,
        `target: ${target}, property: ${property}`
      );
      console.error(errorMessage);
    }

    return editSort(target, property, value.toLowerCase() as DatabaseSortDirection);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in edit_sort operation: ${errorMessage}`,
      CsvErrorCategory.SORT,
      `target: ${row['target']}, property: ${row['property']}, value: ${row['value']}`
    );
    console.error('Error in edit_sort operation', error);
    throw error;
  }
}

function parseDeleteSortOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    return removeSort(row['target'], row['property']);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in delete_sort operation: ${errorMessage}`,
      CsvErrorCategory.SORT,
      `target: ${row['target']}, property: ${row['property']}`
    );
    console.error('Error in delete_sort operation', error);
    throw error;
  }
}

function parseDeleteFieldOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    return removeField(row['target']);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in delete_field operation: ${errorMessage}`,
      CsvErrorCategory.FIELD,
      `target: ${row['target']}`
    );
    console.error('Error in delete_field operation', error);
    throw error;
  }
}

function parseDeleteViewOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    return removeView(row['target']);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in delete_view operation: ${errorMessage}`,
      CsvErrorCategory.VIEW,
      `target: ${row['target']}`
    );
    console.error('Error in delete_view operation', error);
    throw error;
  }
}

function parseAddFilterOperation(
  row: ParsedRow,
  errorCollector?: CsvErrorCollector
): AddFilterToken {
  try {
    const viewName = row['target'];
    const filterString = row['value'];

    if (!viewName) {
      const errorMessage = 'Missing target view for add_filter operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.FILTER,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
    }

    const parsedFilter = FilterLang.Statement.tryParse(filterString);

    return {
      ...parsedFilter,
      viewName,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in add_filter operation: ${errorMessage}`,
      CsvErrorCategory.FILTER,
      `target: ${row['target']}, value: ${row['value']}`
    );
    console.error('Error in add_filter operation', error);
    throw error;
  }
}

function parseEditFilterOperation(
  row: ParsedRow,
  errorCollector?: CsvErrorCollector
): EditFilterToken {
  try {
    const viewName = row['target'];
    const filterString = row['value'];

    if (!viewName) {
      const errorMessage = 'Missing target view for edit_filter operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.FILTER,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
    }

    const parsedFilter = FilterLang.Statement.tryParse(filterString);

    return {
      ...parsedFilter,
      tokenType: TokenType.EDIT_FILTER,
      viewName,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in edit_filter operation: ${errorMessage}`,
      CsvErrorCategory.FILTER,
      `target: ${row['target']}, value: ${row['value']}`
    );
    console.error('Error in edit_filter operation', error);
    throw error;
  }
}

function parseDeleteFilterOperation(row: ParsedRow, errorCollector?: CsvErrorCollector) {
  try {
    return removeFilter(row['target']);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in delete_filter operation: ${errorMessage}`,
      CsvErrorCategory.FILTER,
      `target: ${row['target']}`
    );
    console.error('Error in delete_filter operation', error);
    throw error;
  }
}

function* parseAddViewOperation(
  row: ParsedRow,
  errorCollector?: CsvErrorCollector
): Generator<Token> {
  try {
    const viewName = row['target'];

    if (!viewName) {
      const errorMessage = 'Missing target view for add_view operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.VIEW,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
    }

    yield view(viewName);

    if (row['property']) {
      const layoutType = stringToDatabaseLayoutType(row['property']);
      if (layoutType) {
        yield layout(layoutType, viewName, row['value']);
      } else {
        const errorMessage = `Invalid layout type: ${row['property']}`;
        errorCollector?.addError(
          errorMessage,
          CsvErrorCategory.LAYOUT,
          `view: ${viewName}, property: ${row['property']}, value: ${row['value']}`
        );
        console.error(errorMessage);
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in add_view operation: ${errorMessage}`,
      CsvErrorCategory.VIEW,
      `target: ${row['target']}, property: ${row['property']}, value: ${row['value']}`
    );
    console.error('Error in add_view operation', error);
  }
}

function parseAddHiddenFieldOperation(
  row: ParsedRow,
  errorCollector?: CsvErrorCollector
) {
  try {
    const viewName = row['target'];
    const valueString = row['value'];

    if (!viewName) {
      const errorMessage = 'Missing view name (target) for add_hidden_field operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.VIEW,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    if (!valueString) {
      const errorMessage = `Missing value for add_hidden_field operation on view '${viewName}'`;
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.VIEW,
        `target: ${viewName}, row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    try {
      const fieldNames: string[] = HiddenFieldsLang.Statement.tryParse(valueString);
      if (fieldNames.length === 0) {
        const errorMessage = `Empty field list for add_hidden_field operation on view '${viewName}'`;
        errorCollector?.addError(
          errorMessage,
          CsvErrorCategory.VIEW,
          `target: ${viewName}, value: ${valueString}`
        );
        console.error(errorMessage);
      }
      return addHiddenField(viewName, fieldNames);
    } catch (parseError) {
      const errorMessage =
        parseError instanceof Error ? parseError.message : String(parseError);
      errorCollector?.addError(
        `Error parsing hidden fields for add_hidden_field: ${errorMessage}`,
        CsvErrorCategory.VIEW,
        `target: ${viewName}, value: ${valueString}`
      );
      console.error('Error parsing hidden fields for add_hidden_field', parseError);
      throw parseError;
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in add_hidden_field operation: ${errorMessage}`,
      CsvErrorCategory.VIEW,
      `row: ${JSON.stringify(row)}`
    );
    console.error('Error in add_hidden_field operation', error);
    throw error;
  }
}

function parseRemoveHiddenFieldOperation(
  row: ParsedRow,
  errorCollector?: CsvErrorCollector
) {
  try {
    const viewName = row['target'];
    const fieldName = row['property'];

    if (!viewName) {
      const errorMessage = 'Missing view name (target) for remove_hidden_field operation';
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.VIEW,
        `row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    if (!fieldName) {
      const errorMessage = `Missing field name (property) for remove_hidden_field operation on view '${viewName}'`;
      errorCollector?.addError(
        errorMessage,
        CsvErrorCategory.VIEW,
        `target: ${viewName}, row: ${JSON.stringify(row)}`
      );
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    return removeHiddenField(viewName, [fieldName]);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    errorCollector?.addError(
      `Error in remove_hidden_field operation: ${errorMessage}`,
      CsvErrorCategory.VIEW,
      `row: ${JSON.stringify(row)}`
    );
    console.error('Error in remove_hidden_field operation', error);
    throw error;
  }
}
